import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { Descendant, Transforms } from 'slate';
import ContextEditor, { ContextEditorProps } from 'views/Setting/AutoEmit/components/ContextEditor';
import { TemplateListParams } from 'views/Setting/AutoEmit/Refund/api';
import { ScenarioType, useGetConcepts } from './TranslateTool/utils';
import { isAeProduct } from 'productLine';
import { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import KwaiStickerPicker from 'views/Conversation/Chat/components/MessageEditor/Toolbar/KwaiStickerPicker';
import { ReactEditor, useSlate } from 'slate-react';
import { Button } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

export interface ExecuteContextProps
  extends Omit<TemplateListParams, 'scenarioType' | 'platform'>,
    Omit<ContextEditorProps, 'region' | 'context' | 'onContextChange' | 'limit'> {
  scenarioType?: ScenarioType;
  platform?: General.PlatformUpperCase;
  executeContext: Marketing.ExecuteContext<Descendant[]>;
  onExecuteContextChange: (executeContext: Marketing.ExecuteContext<Descendant[]>) => void;
  replyOption?: Marketing.ReplyOption;
  extraConcepts?: Concept[];
  disabled?: boolean;
}

function ExecuteContext(props: ExecuteContextProps) {
  const {
    platform,
    scenarioType,
    region,
    sendPoint,
    shippingStatus,
    executeContext,
    onExecuteContextChange,
    supportTranslate,
    replyOption,
    extraConcepts,
    leftExtra,
    ...otherProps
  } = props;

  const concepts = useGetConcepts(
    platform || false,
    scenarioType || false,
    replyOption,
    extraConcepts,
  );

  const limit = (() => {
    if (
      isTiKTokUpperCase(platform) &&
      (scenarioType === 'NEGATIVE_REVIEW_REPLY' || scenarioType === 'POSITIVE_REVIEW_REPLY')
    ) {
      return 300;
    }

    if (platform) {
      return platformExecuteContentLimitLengthMap[platform];
    }

    if (isAeProduct) {
      return platformExecuteContentLimitLengthMap.ALIEXPRESS;
    }

    const { ALIEXPRESS, MERCADO, ...others } = platformExecuteContentLimitLengthMap;

    return Math.min(...Object.values(others));
  })();

  return (
    <ContextEditor
      platform={platform}
      region={region}
      context={executeContext}
      onContextChange={onExecuteContextChange}
      limit={limit}
      allowDividing
      concepts={concepts}
      showToolbar={scenarioType !== undefined}
      supportTranslate={supportTranslate}
      leftExtra={leftExtra || (platform === 'KWAI' && <EditorKwaiStickerPicker />)}
      {...otherProps}
    />
  );
}

function EditorKwaiStickerPicker() {
  const [visible, setVisible] = useState(false);

  const editor = useSlate();

  const selectionRef = useRef(editor.selection);

  useEffect(() => {
    if (visible) {
      selectionRef.current = editor.selection;
    }
  }, [visible, editor]);

  const onPick = useCallback(
    (sticker: string) => {
      setVisible(false);

      const { current: selection } = selectionRef;
      if (selection) {
        Transforms.select(editor, selection);
      }
      Transforms.insertText(editor, sticker);
      ReactEditor.focus(editor);
    },
    [editor],
  );

  return (
    <KwaiStickerPicker
      trigger="hover"
      visible={visible}
      onVisibleChange={setVisible}
      onPick={onPick}
    >
      <Button type="link" icon={<SmileOutlined />} />
    </KwaiStickerPicker>
  );
}

export default ExecuteContext;
